import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Table, Button, Space, Tag, Input, Modal, Form,
    message, Popconfirm, List, Typography, ColorPicker, Divider
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ArrowLeftOutlined,
    IdcardOutlined,
    UserOutlined
} from '@ant-design/icons';
import { tagService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;

const VIEW_MODES = {
    LIST: 'LIST',
    CARDS: 'CARDS'
};

const TagsDrawer = ({ visible, onClose, onSelectParticipant }) => {
    const auth = useAuth() || {};
    const permissions = Array.isArray(auth.permissions) ? auth.permissions : [];
    const canEdit = permissions.includes('participants.tags.edit');

    const [viewMode, setViewMode] = useState(VIEW_MODES.LIST);
    const [loading, setLoading] = useState(false);
    const [tags, setTags] = useState([]);
    const [selectedTag, setSelectedTag] = useState(null);
    const [tagCards, setTagCards] = useState([]);

    // Modal State
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [form] = Form.useForm();

    const fetchTags = useCallback(async () => {
        setLoading(true);
        try {
            const response = await tagService.list();
            if (response.status === 'success') {
                setTags(response.data);
            }
        } catch (error) {
            message.error('Error al cargar tags');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            fetchTags();
            setViewMode(VIEW_MODES.LIST);
            setSelectedTag(null);
        }
    }, [visible, fetchTags]);

    const handleViewCards = async (tag, search = null) => {
        setLoading(true);
        setSelectedTag(tag);
        setViewMode(VIEW_MODES.CARDS);
        try {
            const response = await tagService.getTagCards(tag.id, search);
            if (response.status === 'success') {
                setTagCards(response.data.items || []);
            }
        } catch (error) {
            message.error('Error al cargar tarjetas del tag');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (mode, record = null) => {
        setModalMode(mode);
        if (mode === 'edit' && record) {
            form.setFieldsValue({
                name: record.name,
                color: record.color || '#1890ff'
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ color: '#1890ff' });
        }
        setModalVisible(true);
    };

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            // Convert color to hex if it's an object from ColorPicker
            const color = typeof values.color === 'string' ? values.color : values.color.toHexString();
            const payload = { ...values, color };

            if (modalMode === 'create') {
                await tagService.create(payload);
                message.success('Tag creado exitosamente');
            } else {
                await tagService.update(selectedTag.id, payload);
                message.success('Tag actualizado exitosamente');
                // Update selected tag if we are in CARDS view
                if (selectedTag) setSelectedTag({ ...selectedTag, ...payload });
            }
            setModalVisible(false);
            fetchTags();
        } catch (error) {
            if (error.errorFields) return;
            message.error('Error al guardar el tag');
        }
    };

    const handleDeleteTag = async (id) => {
        try {
            await tagService.delete(id);
            message.success('Tag eliminado');
            fetchTags();
        } catch (error) {
            message.error('Error al eliminar el tag');
        }
    };

    const handleGoToParticipant = (participantUuid) => {
        onSelectParticipant(participantUuid);
        onClose();
    };

    const columns = [
        {
            title: 'Nombre',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Tag color={record.color || 'blue'} style={{ fontSize: '14px', padding: '4px 10px' }}>
                    {text}
                </Tag>
            )
        },
        {
            title: 'Tarjetas',
            dataIndex: 'card_count',
            key: 'card_count',
            align: 'center',
            render: (count) => <Text strong>{count || 0}</Text>
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <Space>
                    <Button
                        type="text"
                        icon={<IdcardOutlined />}
                        onClick={() => handleViewCards(record)}
                        title="Ver Tarjetas"
                    />
                    {canEdit && (
                        <>
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    setSelectedTag(record);
                                    handleOpenModal('edit', record);
                                }}
                            />
                            <Popconfirm
                                title="¿Eliminar este tag?"
                                description={record.card_count > 0 ? "Este tag tiene tarjetas vinculadas. Se recomienda no eliminarlo." : "¿Estás seguro de eliminar este tag?"}
                                okText="Sí, Eliminar"
                                cancelText="Cancelar"
                                okType="danger"
                                disabled={record.card_count > 0}
                                onConfirm={() => handleDeleteTag(record.id)}
                            >
                                <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={record.card_count > 0}
                                />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <>
            <Drawer
                title={
                    viewMode === VIEW_MODES.LIST ? "Gestión de Tags" : (
                        <Space>
                            <Button
                                type="text"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => setViewMode(VIEW_MODES.LIST)}
                            />
                            <span>Tarjetas: <Tag color={selectedTag?.color}>{selectedTag?.name}</Tag></span>
                        </Space>
                    )
                }
                placement="right"
                width={550}
                onClose={onClose}
                open={visible}
                extra={
                    viewMode === VIEW_MODES.LIST && canEdit && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => handleOpenModal('create')}
                        >
                            Nuevo Tag
                        </Button>
                    )
                }
            >
                {viewMode === VIEW_MODES.LIST ? (
                    <Table
                        dataSource={tags}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        size="small"
                    />
                ) : (
                    <>
                        <div style={{ marginBottom: '16px' }}>
                            <Input.Search
                                placeholder="Buscar por nombre de participante..."
                                onSearch={(value) => handleViewCards(selectedTag, value)}
                                allowClear
                            />
                        </div>
                        <List
                            loading={loading}
                            dataSource={tagCards}
                            locale={{ emptyText: "No hay tarjetas con este tag." }}
                            renderItem={item => (
                                <div style={{
                                    border: '1px solid #f0f0f0',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '16px',
                                    backgroundColor: '#fff',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Title level={5} style={{ margin: 0 }}>{item.title}</Title>
                                        <Button
                                            type="link"
                                            size="small"
                                            onClick={() => handleGoToParticipant(item.participant_uuid)}
                                        >
                                            Ir a participante
                                        </Button>
                                    </div>
                                    <Divider style={{ margin: '8px 0' }} />
                                    <div style={{ marginBottom: '10px' }}>
                                        <Text type="secondary">{item.description}</Text>
                                    </div>
                                    <Space align="center" style={{ fontSize: '12px' }}>
                                        <UserOutlined />
                                        <Text strong>{item.participant_name}</Text>
                                    </Space>
                                </div>
                            )}
                        />
                    </>
                )}
            </Drawer>

            <Modal
                title={modalMode === 'create' ? "Nuevo Tag" : "Editar Tag"}
                open={modalVisible}
                onOk={handleModalSubmit}
                onCancel={() => setModalVisible(false)}
                okText="Guardar"
                cancelText="Cancelar"
                centered
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Nombre del Tag"
                        name="name"
                        rules={[{ required: true, message: 'Ingrese el nombre' }]}
                    >
                        <Input placeholder="Ej. Salud, Educación, Vivienda..." />
                    </Form.Item>
                    <Form.Item
                        label="Color Predeterminado"
                        name="color"
                        rules={[{ required: true }]}
                    >
                        <ColorPicker showText />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default TagsDrawer;
